import { useFormikContext } from "formik";
import i18next from "i18next";
import React from "react";
import { useTranslation } from "react-i18next";

import {
  Checkbox,
  FormControl,
  Loader,
  Text,
} from "@noom/wax-component-library";

import { OPT_OUT_DISABLED_PARTNERS } from "@/constants/Partners";
import { useAppContext } from "@/contexts";
import { useGeolocation } from "@/hooks";
import { getDisplayName } from "@/models";
import { isB2CPartner } from "@/utils/partners";

import type { CreateAccountFormValues } from "../forms";

type GenericConsentProps = {
  ctaText: string;
  onCheckboxChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  showGinaAdaCheckBox?: boolean;
};

export const GenericConsent: React.FC<GenericConsentProps> = ({
  ctaText,
  onCheckboxChange,
  showGinaAdaCheckBox = false,
}) => {
  const { partnerInfo } = useAppContext();
  const { errors, getFieldProps, touched, values } =
    useFormikContext<CreateAccountFormValues>();
  const { canOptOut, canOptOutMHMDA, isLoading } = useGeolocation();
  const { t } = useTranslation();

  const getTosHtml = () => {
    const hasCustomCopy = i18next.exists(
      `termsOfService.partners.${partnerInfo?.slug}`,
    );

    const shouldShowGeoCopy =
      canOptOut() &&
      !OPT_OUT_DISABLED_PARTNERS.includes(partnerInfo?.slug || "");

    const yourPrivacyChoices = t("termsOfService.yourPrivacyChoices");

    if (hasCustomCopy) {
      const customTos = t(`termsOfService.partners.${partnerInfo?.slug}`, {
        signUpButton: ctaText,
      });

      if (shouldShowGeoCopy) {
        return `${customTos} ${yourPrivacyChoices}`;
      }

      return customTos;
    }

    const baseTos = t("termsOfService.default", {
      context: showGinaAdaCheckBox ? "ginaAda" : undefined,
      signUpButton: ctaText,
    });

    const noomMayShareHtml = t("termsOfService.noomMayShareInformation", {
      partnerName: getDisplayName(partnerInfo),
    });

    if (shouldShowGeoCopy) {
      return `${baseTos} ${noomMayShareHtml}${yourPrivacyChoices}`;
    }

    return baseTos;
  };

  if (isLoading) {
    return <Loader size="xl" colorScheme="primary" />;
  }

  if (isB2CPartner(partnerInfo)) {
    const translationKey = canOptOutMHMDA()
      ? "termsOfService.b2cMHMDA"
      : "termsOfService.b2c";

    return (
      <Text
        dangerouslySetInnerHTML={{
          __html: t(translationKey, { signUpButton: ctaText }),
        }}
      />
    );
  }

  if (showGinaAdaCheckBox) {
    const ginaAdaDisclaimer = getTosHtml();

    return (
      <FormControl error={touched.ginaAdaConsent && errors.ginaAdaConsent}>
        <Checkbox
          {...getFieldProps("ginaAdaConsent")}
          isChecked={values.ginaAdaConsent === true}
          onChange={onCheckboxChange}
        >
          <Text
            dangerouslySetInnerHTML={{ __html: ginaAdaDisclaimer }}
            size="sm"
          />
        </Checkbox>
      </FormControl>
    );
  }

  return (
    <Text
      dangerouslySetInnerHTML={{
        __html: getTosHtml(),
      }}
      size="sm"
    />
  );
};
